import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { Story, useStoryUpdateFollowedStatusMutation } from '../../services';

export const useStoryFollow = () => {
  const { t } = useTranslation();

  const [updateFollowedStatus] = useStoryUpdateFollowedStatusMutation();

  const [followedById, setFollowedById] = useState<Record<number, boolean>>({});

  const [isFollowPending, setIsFollowPending] = useState(false);

  const isStoryFollowed = useCallback(
    ({ id, feedFollowedStatus }: Story) => followedById[id] ?? feedFollowedStatus,
    [followedById]
  );

  const toggleStoryFollow = useCallback(
    async (story: Story) => {
      const { id: storyId } = story;

      const status = !isStoryFollowed(story);

      setFollowedById((followedByIdPrev) => ({ ...followedByIdPrev, [storyId]: status }));
      setIsFollowPending(true);

      try {
        await updateFollowedStatus({ storyId, status }).unwrap();

        return true;
      } catch (_) {
        setFollowedById((followedByIdPrev) => ({ ...followedByIdPrev, [storyId]: !status }));
        toast.error(t('common.error-message'));

        return false;
      } finally {
        setIsFollowPending(false);
      }
    },
    [isStoryFollowed, t, updateFollowedStatus]
  );

  return useMemo(
    () => ({ isStoryFollowed, toggleStoryFollow, isFollowPending }),
    [isFollowPending, isStoryFollowed, toggleStoryFollow]
  );
};
